import { useState, useContext } from "react";
import {
  Typography,
  Button,
  Form,
  Row,
  Col,
  Select,
  Space,
  Divider,
  Card,
} from "antd";
import {
  QuestionCircleTwoTone,
  AppstoreOutlined,
  SettingOutlined,
  ToolOutlined,
  UserOutlined,
} from "@ant-design/icons";

import { SpecSettingContext } from "../../../pages/SpecSetting";
import SyncRuleStepDrawer from "./SyncRuleStepDrawer";

const { Title, Text } = Typography;

const SyncRuleStep = ({ approval }) => {
  const { blueprint, updateSettingBlueprint } = useContext(SpecSettingContext);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [syncRuleForm] = Form.useForm();

  const matchingRule = [
    { value: "contains", label: "包含匹配" },
    { value: "exact", label: "完全匹配" },
  ];

  const matchingAction = [
    { value: "create", label: "自動新增" },
    { value: "null", label: "填入空值" },
    { value: "skip", label: "跳過此工單" },
    { value: "manual", label: "標記為需人工處理" },
  ];

  const matchingRow = (target) => (
    <>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={[target, "matchingField"]}
            label="匹配欄位"
            rules={[{ required: true, message: "請選擇匹配欄位" }]}
          >
            <Select
              mode="multiple"
              options={
                target === "device"
                  ? [{ value: "deviceName", label: "設備名稱" }]
                  : target === "station"
                  ? [{ value: "stationName", label: "報工站名稱" }]
                  : target === "worker"
                  ? [
                      { value: "workerId", label: "員工編號" },
                      { value: "workerName", label: "員工姓名" },
                    ]
                  : target === "product"
                  ? [
                      { value: "productNumber", label: "物料編號" },
                      { value: "productName", label: "物料名稱" },
                    ]
                  : []
              }
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={[target, "matchingStrategy"]}
            label="匹配策略"
            rules={[{ required: true, message: "請選擇匹配策略" }]}
          >
            <Select placeholder="請選擇匹配策略" options={matchingRule} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={[target, "fallbackAction"]}
            label="找不到匹配時的處理方式"
            rules={[{ required: true, message: "請選擇處理方式" }]}
          >
            <Select placeholder="請選擇處理方式" options={matchingAction} />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      <SyncRuleStepDrawer open={drawerOpen} setOpen={setDrawerOpen} />
      <Title level={4}>
        工單同步規則
        <Button
          type="text"
          shape="circle"
          icon={<QuestionCircleTwoTone />}
          onClick={() => setDrawerOpen(true)}
        />
      </Title>
      <Text type="secondary">
        設定 ERP 系統內匹配特定規則的工單，即可同步匯入至 SFR
        系統，包括設備、員工、物料和途程工序等匹配策略。
        <br />
        此外，可設定當找不到匹配項目時的自動處理方式。
        <br />
        <br />
        <div className="warning-block">注意：達易僅同步未開工工單。</div>
      </Text>

      <Form
        form={syncRuleForm}
        disabled={approval}
        layout="vertical"
        initialValues={blueprint["syncRule"]}
        onValuesChange={(values) => updateSettingBlueprint("syncRule", values)}
        style={{ marginTop: 24 }}
      >
        {/* 設備匹配規則 */}
        <Card
          className="section-card"
          title={
            <Space>
              <ToolOutlined />
              設備匹配規則
            </Space>
          }
        >
          <div className="information-block">
            <strong>設備匹配說明：</strong>
            由於 SFR
            與客戶端的設備名稱可能有差異，系統預設會使用機台名稱進行包含匹配的方式來找到對應的設備，若找不到對應設備，系統預設自動新增。
          </div>
          {matchingRow("device")}
          <div className="description-block">
            <strong>匹配範例：</strong>
            <br />
            • ERP 設備名稱："CNC-001" → SFR
            設備名稱："CNC-001-車床"（包含匹配成功）
            <br />• 如果找不到匹配的設備，系統會根據設定的處理方式來決定後續動作
          </div>
        </Card>
        {/* 報工站匹配規則 */}
        <Card
          className="section-card"
          title={
            <Space>
              <ToolOutlined />
              報工站匹配規則
            </Space>
          }
        >
          <div className="information-block">
            <strong>報工站匹配說明：</strong>
            預設使用報工站名稱進行包含匹配的方式來找到對應的報工站，若找不到對應報工站，系統預設填入空值。
          </div>
          {matchingRow("station")}
        </Card>
        {/* 員工匹配規則 */}
        <Card
          className="section-card"
          title={
            <Space>
              <UserOutlined />
              員工匹配規則
            </Space>
          }
        >
          <div className="information-block">
            <strong>員工匹配說明：</strong>
            預設使用員工編號進行精確匹配，如果找不到對應的員工，且 ERP
            有提供員工編號與員工名稱則自動新增。
          </div>
          {matchingRow("worker")}
        </Card>
        {/* 物料匹配規則 */}
        <Card
          className="section-card"
          title={
            <Space>
              <AppstoreOutlined />
              物料匹配規則
            </Space>
          }
        >
          <div className="information-block">
            <strong>物料匹配說明：</strong>
            預設使用物料編號與物料名稱進行精確匹配，如果找不到對應的物料即自動新增。
          </div>
          {matchingRow("product")}
          <div className="warning-block">
            <strong>注意：</strong>自動新增物料時需要確保有完整的物料資訊，
            包括物料編號、物料名稱等基本資訊。
          </div>
          <br />
          {/* 途程工序匹配規則 */}
          <Card
            className="section-card"
            title={
              <Space>
                <SettingOutlined />
                途程工序匹配規則
              </Space>
            }
          >
            <div className="information-block">
              <strong>途程工序匹配說明：</strong>
              物料必須已經同步過，才會進行途程工序的匹配。
            </div>

            <Divider orientation="left">工序匹配設定</Divider>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={["operation", "matchingField"]}
                  label="工序匹配欄位"
                  rules={[{ required: true, message: "請選擇匹配欄位" }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="選擇用於匹配的欄位"
                    options={[
                      { value: "opCode", label: "工序編號" },
                      { value: "opName", label: "工序名稱" },
                      { value: "opOrder", label: "工序" },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={["operation", "matchingStrategy"]}
                  label="匹配策略"
                  rules={[{ required: true, message: "請選擇匹配策略" }]}
                >
                  <Select
                    placeholder="請選擇匹配策略"
                    options={[{ value: "exact", label: "完全匹配" }]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div className="description-block">
              <strong>匹配邏輯：</strong>
              <br />
              • 系統會比較物料下所有途程的工序數量與內容
              <br />
              • 預設必須所有工序編號、工序名稱、工序都完全一致才算匹配成功
              <br />• 如果沒有完全匹配的途程，預設會自動新增途程
            </div>
          </Card>
        </Card>
      </Form>
    </div>
  );
};

export default SyncRuleStep;
